import axios from 'axios';
import apiUrlOne from '../utils.js/Config';

/**
 * Normalize displayOrder values in an object or array.
 * - If displayOrder is 0 or "0", convert to 1
 * - All other values remain unchanged
 * - Handles nested objects and arrays recursively
 * - Does not mutate the original object (deep clone)
 */
const normalizeDisplayOrder = (data) => {
    if (data === null || data === undefined) {
        return data;
    }

    // Handle arrays - recursively normalize each element
    if (Array.isArray(data)) {
        return data.map(item => normalizeDisplayOrder(item));
    }

    // Handle objects - check for displayOrder and recursively process nested properties
    if (typeof data === 'object') {
        const normalized = { ...data };

        // Normalize displayOrder if it exists
        if (normalized.hasOwnProperty('displayOrder')) {
            const value = normalized.displayOrder;
            const numericValue = Number(value);
            // Check if value is 0 (either number or string "0")
            if (numericValue === 0 && value !== '') {
                normalized.displayOrder = 1;
            } else {
                normalized.displayOrder = value;
            }
        }

        // Recursively normalize all nested properties
        for (const key in normalized) {
            if (normalized.hasOwnProperty(key) && typeof normalized[key] === 'object') {
                normalized[key] = normalizeDisplayOrder(normalized[key]);
            }
        }

        return normalized;
    }

    // Return primitive values as-is
    return data;
};

// Create axios instance with base URL
const axiosInstance = axios.create({
    baseURL: apiUrlOne,
});

// Request interceptor to add Authorization header
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle token refresh or logout on 401
// Also normalizes displayOrder values to show 1 instead of 0
axiosInstance.interceptors.response.use(
    (response) => {
        // Normalize displayOrder values in the response data
        if (response.data) {
            response.data = normalizeDisplayOrder(response.data);
        }
        return response;
    },
    (error) => {
        if (error.response) {
            error.status = error.response.status;
            error.backendMessage = error.response.data?.message;
            error.apiPath = error.response.data?.path || error.config?.url;
        }

        if (error.response && error.response.status === 401) {
            // Token expired, clear localStorage and redirect to login
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            // Optionally redirect to login page
            // window.location.href = '/login';
        }
        // Note: 403 errors are handled at the component/route level
        // We do not automatically redirect or replace UI from the interceptor
        // to allow components to handle permission denials appropriately
        return Promise.reject(error);
    }
);

export default axiosInstance;
